import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { PaymentReplacementReasonTypes, PaymentStatusType } from '@/constants/paymentStatuses';
import { useBuyCrypto } from '@/store/buyCrypto';
import { PaymentType, TxnWSMessage } from '@/api/common.types';
import SockJsClient from 'react-stomp';
import { useGetBuyTxn } from '@/api/queryHooks/useBuyCryptoController';
import { useReadLocalStorage } from 'usehooks-ts';
import { ACCESS_TOKEN } from '@/constants/storage';
import { CheckoutStatusPopups } from '@/components/common/popups/CheckoutStatus';
import { useGetReplacedTxInfo } from '@/hooks/useGetReplacedTxInfo';
import { CryptoFrontendStatus } from '@/api/queryHooks/useTransactionsController';
import { truncateString } from '@/utils/functions';
import { mapTxTypeDtoToTxType } from '@/mappers';
import { useGetRating, useSetRating } from '@/api';
import { OverlayProvider } from 'react-aria';
import { Popup } from '@/components/common';
import { Rating } from '@/components/common/popups/Rating';
import { useGetCryptoNetworkData } from '@/hooks/useGetCryptoNetworkData';
import { WaitingPaymentLoader } from '../WaitingPaymentLoader';
import { CopyButton } from '../../../../components/common/buttons';
import styles from './BuyCheckoutData.module.css';

interface BuyCheckoutDataProps {
  status: PaymentStatusType;
  retryTx: () => void;
  cancelTxn: () => void;
  isCancelBuyTxnLoading?: boolean;
}

export const BuyCheckoutData = ({ status, retryTx, cancelTxn, isCancelBuyTxnLoading }: BuyCheckoutDataProps) => {
  const buyState = useBuyCrypto((state) => state, shallow);
  const ref = useRef(null);
  const accessToken = useReadLocalStorage(ACCESS_TOKEN);
  const { setReplacedTxId, replacedTxInfo } = useGetReplacedTxInfo(true);
  const [isUnderPaid, setIsUnderPaid] = useState(false);

  const { refetch: getTransaction } = useGetBuyTxn([{ id: buyState.txData?.id }, { enabled: false }]);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);

  const { data: selectedCoinNetworksData } = useGetCryptoNetworkData({
    cryptoCurrency: buyState?.txData?.cryptoCurrency!,
    enabled: !!buyState?.txData?.cryptoCurrency,
  });

  const selectedNetworkData = useMemo(() => {
    return selectedCoinNetworksData?.find((network) => network.cryptoNetwork === buyState.txData?.cryptoNetwork);
  }, [selectedCoinNetworksData, buyState.txData?.cryptoNetwork]);

  // Need this to manually set the status of the transaction and open the "expired" popup
  const [manualTxStatus, setManualTxStatus] = useState<PaymentStatusType | null>(null);
  // Reset the manual status when the status changes
  useEffect(() => {
    setManualTxStatus(null);
  }, [status]);

  const { data: ratingData, refetch: refetchRating } = useGetRating();
  const { mutate: setRate } = useSetRating({
    onSuccess() {
      refetchRating();
    },
  });

  const onRateHandler = () => {
    try {
      setRate();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
  const onRateModalClose = () => {
    setIsRatingModalOpen(false);
  };
  useEffect(() => {
    if (ratingData?.shouldAskForRating && status === PaymentStatusType.Completed) {
      setIsRatingModalOpen(true);
    }
  }, [ratingData?.shouldAskForRating, status]);

  return (
    <div className={styles.checkoutDataWrapper}>
      {!!buyState.txData && (
        <SockJsClient
          url={`${process.env.NEXT_PUBLIC_API_BASE_URL}/websocket?token=${accessToken}`}
          topics={[`/topic/txs/${buyState.txData?.id}`]}
          onMessage={(message: TxnWSMessage) => {
            if (message.txStatus === CryptoFrontendStatus.REPLACED && message.replacedBy) {
              setReplacedTxId(message.replacedBy);
            }
            if (message.replacementReason === PaymentReplacementReasonTypes.UnderPaid) {
              setIsUnderPaid(true);
            } else {
              setIsUnderPaid(false);
            }
            getTransaction();
          }}
        />
      )}

      <CheckoutStatusPopups
        status={manualTxStatus || status}
        onRetry={retryTx}
        replacedTxInfo={replacedTxInfo}
        transactionData={{
          id: String(buyState?.txData?.id || ''),
          amount: String(buyState?.txData?.fiatAmount || ''),
          crypto: String(buyState?.txData?.cryptoCurrency || ''),
          type: (mapTxTypeDtoToTxType(buyState?.txData?.paymentType) as PaymentType) || '',
        }}
        flowType="buy"
        explorerUrl={selectedNetworkData?.explorerUrl}
      />

      <div className={styles.row}>
        <div className={styles.checkoutDataInner}>
          <div className={styles.currency}>
            <div className={styles.cell}>
              <span className={styles.checkoutDataTitle}>You Pay:</span>
              <div className={styles.value}>
                ${buyState.txData?.fiatAmount} {buyState.txData?.fiatCurrency}
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.cell}>
              <span className={styles.checkoutDataTitle}>You Receive:</span>
              <div className={styles.value}>
                {buyState.txData?.cryptoAmount} {buyState.get}{' '}
              </div>
              <div className={styles.rate}>
                1 {buyState.get} = ${buyState.rate} {buyState.txData?.fiatCurrency}
              </div>
            </div>
          </div>
          <div className={styles.infoWrapper}>
            {buyState.typeOfPayment === PaymentType.PAY_ID_IN ? (
              <div
                ref={ref}
                className={clsx(styles.cell, {
                  [styles.payIdCell]: buyState.typeOfPayment === PaymentType.PAY_ID_IN,
                })}
              >
                <span className={styles.checkoutDataTitle}>RelayPay PayID:</span>
                <span className={styles.info}>{buyState.txData?.depositPayId}</span>
              </div>
            ) : (
              <div className={styles.cells} ref={ref}>
                <div className={styles.cell}>
                  <span className={styles.checkoutDataTitle}>Account Name:</span>
                  <span className={styles.info}>RelayPay</span>
                </div>
                <div className={styles.cell}>
                  <span className={styles.checkoutDataTitle}>BSB:</span>
                  <span className={styles.info}>{buyState.txData?.depositBsb}</span>
                </div>
                <div className={styles.cell}>
                  <span className={styles.checkoutDataTitle}>Account Number:</span>
                  <span className={styles.info}>{buyState.txData?.depositAccountNumber}</span>
                </div>
              </div>
            )}
            <CopyButton iconPosition="left" variants="details" data={ref}>
              Copy Details
            </CopyButton>
          </div>
          <div>
            <p className={styles.warning}>
              <span className={styles.bold}>Important:</span> Scammers targeting victims with sophisticated investment
              scam schemes and asking crypto transfers. Crypto transactions are{' '}
              <span className={styles.underlined}>not reversible.</span> Please read our{' '}
              <a
                target="_blank"
                className={styles.warningLink}
                href="https://www.relaypay.io/post/crypto-investment-scams-australia-how-to-avoid"
                rel="noreferrer"
              >
                blog post
              </a>{' '}
              to spot scammers and stay safe.
            </p>
          </div>
        </div>

        <WaitingPaymentLoader
          onCounterComplete={() => setManualTxStatus(PaymentStatusType.Expired)}
          status={manualTxStatus || status}
          className={styles.loader}
          retryTx={retryTx}
          isUnderPaid={isUnderPaid}
          cancelTxn={cancelTxn}
          isCancelBuyTxnLoading={isCancelBuyTxnLoading}
        />
      </div>
      <div className={styles.summary}>
        <div className={clsx(styles.summaryTitle, styles.mainTitle)}>Summary:</div>
        <div className={clsx(styles.summaryRow, styles.summaryRowBorder)}>
          <span className={styles.summarySubtitle}>
            {status === PaymentStatusType.Completed ? 'Transaction Hash:' : 'Your Wallet Address:'}
          </span>
          <CopyButton
            iconPosition="right"
            variants={status === PaymentStatusType.Completed ? 'buyCryptoHash' : 'walletAddress'}
            className={styles.button}
            valueForCopy={
              status === PaymentStatusType.Completed
                ? buyState.txData?.blockchainTxHash
                : buyState.txData?.transferAddress
            }
          >
            {truncateString({
              string:
                status === PaymentStatusType.Completed
                  ? buyState.txData?.blockchainTxHash
                  : buyState.txData?.transferAddress,
            })}
          </CopyButton>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summarySubtitle}>Payment Type:</span>
          <span className={clsx(styles.summaryTitle, styles.summaryTitleMobile)}>
            {buyState.typeOfPayment === PaymentType.PAY_ID_IN ? 'PayID' : 'Bank Transfer'}
          </span>
        </div>
      </div>
      <OverlayProvider>
        <Popup isOpen={isRatingModalOpen} onClose={onRateModalClose} hasCloseIcon dialogClassName={styles.ratingModal}>
          <Rating onRateClick={onRateHandler} />
        </Popup>
      </OverlayProvider>
    </div>
  );
};
